import React from "react";
import styled, { keyframes } from "styled-components";

// Subtle float animation for logos
const float = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-5px) scale(1.05);
  }
`;

// Seamless infinite scroll animation
const scrollInfinite = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

// Shine effect animation
const shine = keyframes`
  0% {
    background-position: -200px;
  }
  100% {
    background-position: 400px;
  }
`;

const StripContainer = styled.div`
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  background: linear-gradient(90deg, 
    rgba(26, 149, 201, 0.05), 
    rgba(26, 149, 201, 0.1),
    rgba(57, 185, 239, 0.15),
    rgba(26, 149, 201, 0.1),
    rgba(26, 149, 201, 0.05)
  );
  border-top: 1px solid rgba(26, 149, 201, 0.15);
  border-bottom: 1px solid rgba(26, 149, 201, 0.15);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 30px 0;
  position: relative;
  margin: 40px 0;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, 
      rgba(255, 255, 255, 0) 0%, 
      rgba(255, 255, 255, 0.05) 25%, 
      rgba(255, 255, 255, 0.1) 50%, 
      rgba(255, 255, 255, 0.05) 75%, 
      rgba(255, 255, 255, 0) 100%);
    animation: ${shine} 8s linear infinite;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 20px 0;
    margin: 30px 0;
  }
`;

const TitleBar = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const StripTitle = styled.h3`
  font-size: 1.2rem;
  color: rgba(26, 149, 201, 0.8);
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0;
`;

// This wrapper contains the duplicate set of logos for seamless scrolling
const InfiniteLogoTrack = styled.div`
  display: inline-block;
  white-space: nowrap;
  animation: ${scrollInfinite} 40s linear infinite;
  will-change: transform;
`;

const LogoContainer = styled.div`
  display: inline-block;
  position: relative;
  margin: 0 25px;
  vertical-align: middle;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.15) translateY(-5px);
    z-index: 2;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  height: 60px;
  padding: 0 15px;
  filter: grayscale(90%);
  opacity: 0.7;
  transition: all 0.3s ease;
  animation: ${float} 3s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
  
  ${LogoContainer}:hover & {
    filter: grayscale(0%);
    opacity: 1;
  }

  @media (max-width: 768px) {
    height: 45px;
    padding: 0 10px;
  }
`;

const LogoText = styled.span`
  font-weight: 700;
  font-size: 1.5rem;
  color: #333;
  
  ${LogoContainer}:hover & {
    color: ${props => props.color || '#1a95c9'};
  }
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const Icon = styled.span`
  font-size: 1.8rem;
  margin-right: 8px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-right: 5px;
  }
`;

// Define AI and tech brands with their icons
const brands = [
  { name: 'OpenAI', icon: '🤖', color: '#10a37f' },
  { name: 'Anthropic', icon: '🔬', color: '#762ce7' },
  { name: 'Google AI', icon: '🧠', color: '#4285f4' },
  { name: 'Azure AI', icon: '☁️', color: '#0078d4' },
  { name: 'TensorFlow', icon: '📊', color: '#ff6f00' },
  { name: 'PyTorch', icon: '🔥', color: '#ee4c2c' },
  { name: 'Hugging Face', icon: '🤗', color: '#ffbd13' },
  { name: 'IBM Watson', icon: '💭', color: '#0f62fe' },
  { name: 'Cohere', icon: '📚', color: '#6149cd' },
  { name: 'DeepMind', icon: '🎮', color: '#00a3e0' },
  { name: 'Midjourney', icon: '🎨', color: '#7289da' },
  { name: 'Nvidia AI', icon: '🖥️', color: '#76b900' }
];

const SoftwareLogoStrip = () => {
  // Create a component with duplicate logo sets for seamless infinite scrolling
  const renderLogos = (isSecondSet = false) => {
    return brands.map((brand, index) => (
      <LogoContainer key={`${isSecondSet ? 'dup-' : ''}${index}`}>
        <Logo delay={(index * 0.2) % 2}>
          <Icon>{brand.icon}</Icon>
          <LogoText color={brand.color}>{brand.name}</LogoText>
        </Logo>
      </LogoContainer>
    ));
  };

  return (
    <StripContainer>
      <TitleBar>
        <StripTitle>Powered by Leading AI Technologies</StripTitle>
      </TitleBar>
      <InfiniteLogoTrack>
        {renderLogos()}
        {renderLogos(true)}
      </InfiniteLogoTrack>
    </StripContainer>
  );
};

export default SoftwareLogoStrip; 